import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

export type ActivityEntry = {
  id: string
  type: string
  emoji: string
  description: string
  detail: string
  timeAgo: string
  timestamp: number
}

type NewActivity = Omit<ActivityEntry, 'id' | 'timeAgo' | 'timestamp'>

interface ActivityCtx {
  activities: ActivityEntry[]
  addActivity: (entry: NewActivity) => void
  hasNew: boolean
  markRead: () => void
}

const ActivityContext = createContext<ActivityCtx>({
  activities: [],
  addActivity: () => {},
  hasNew: false,
  markRead: () => {},
})

export function useActivity() {
  return useContext(ActivityContext)
}

function timeAgoLabel(ts: number): string {
  const diff = Date.now() - ts
  if (diff < 60_000)   return 'just now'
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`
  return `${Math.floor(diff / 3600_000)}h ago`
}

function rowToEntry(row: Record<string, unknown>): ActivityEntry {
  const ts = new Date(row.created_at as string).getTime()
  return {
    id:          row.id as string,
    type:        (row.type        as string) || 'info',
    emoji:       (row.emoji       as string) || '📝',
    description: (row.description as string) || '',
    detail:      (row.detail      as string) || '',
    timeAgo:     timeAgoLabel(ts),
    timestamp:   ts,
  }
}

export function ActivityProvider({ children }: { children: React.ReactNode }) {
  const [activities, setActivities] = useState<ActivityEntry[]>([])
  const [lastRead,   setLastRead]   = useState(0)
  const userIdRef   = useRef<string | null>(null)
  const userNameRef = useRef<string | null>(null)

  // Cache current user info for activity inserts
  useEffect(() => {
    void (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return
        userIdRef.current = user.id
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single()
        if (profile) userNameRef.current = (profile as { full_name?: string }).full_name ?? null
      } catch { /* non-critical */ }
    })()
  }, [])

  // Load recent activities on mount
  useEffect(() => {
    void (async () => {
      try {
        const { data } = await supabase
          .from('activities')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(20)
        if (data) setActivities((data as Record<string, unknown>[]).map(rowToEntry))
      } catch (e) { console.error('ActivityContext load:', e) }
    })()
  }, [])

  // Realtime subscription — picks up inserts from all sessions/tabs
  useEffect(() => {
    const channel = supabase
      .channel('activities-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activities' }, payload => {
        const entry = rowToEntry(payload.new as Record<string, unknown>)
        setActivities(prev => {
          if (prev.some(a => a.id === entry.id)) return prev   // dedup
          return [entry, ...prev.slice(0, 19)]
        })
      })
      .subscribe()
    return () => { void supabase.removeChannel(channel) }
  }, [])

  const addActivity = useCallback((entry: NewActivity) => {
    // Fire-and-forget persist; realtime subscription updates local state
    void (async () => {
      try {
        await supabase.from('activities').insert({
          user_id:     userIdRef.current,
          user_name:   userNameRef.current,
          type:        entry.type,
          emoji:       entry.emoji,
          description: entry.description,
          detail:      entry.detail,
        })
      } catch {
        // DB unavailable — fall back to in-memory only
        const now = Date.now()
        setActivities(prev => [
          { ...entry, id: 'act-' + now, timestamp: now, timeAgo: 'just now' },
          ...prev.slice(0, 19),
        ])
      }
    })()
  }, [])

  const markRead = useCallback(() => setLastRead(Date.now()), [])

  // Refresh timeAgo labels every render (cheap, no interval needed)
  const withFreshLabels = activities.map(a => ({
    ...a,
    timeAgo: timeAgoLabel(a.timestamp),
  }))

  const hasNew = activities.some(a => a.timestamp > lastRead)

  return (
    <ActivityContext.Provider value={{ activities: withFreshLabels, addActivity, hasNew, markRead }}>
      {children}
    </ActivityContext.Provider>
  )
}
